/**
 * useApiTokens — W2.4
 *
 * Verwaltet API-Tokens: Liste, Create, Rotate, Revoke.
 * Endpoints: GET/POST /api/v2/api-tokens, PATCH /api/v2/api-tokens/:id/rotate, DELETE /api/v2/api-tokens/:id
 */
import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Typen ---

export type ApiToken = {
  id: number
  name: string
  last_used_at: string | null
  created_at: string
}

export type ApiTokenWithSecret = ApiToken & {
  token: string
}

type ApiTokensListResponse = {
  data: ApiToken[]
}

type ApiTokenCreateResponse = {
  data: ApiTokenWithSecret
}

type ApiTokenRotateResponse = {
  data: ApiTokenWithSecret
}

// --- Hook ---

type UseApiTokensResult = {
  tokens: ApiToken[]
  isLoading: boolean
  isError: boolean
  isPremiumError: boolean
  refetch: () => void
  createToken: (name: string) => Promise<ApiTokenWithSecret>
  rotateToken: (id: number) => Promise<ApiTokenWithSecret>
  revokeToken: (id: number) => Promise<void>
  isCreating: boolean
  rotatingId: number | null
  revokingId: number | null
}

export function useApiTokens(): UseApiTokensResult {
  const queryClient = useQueryClient()
  const [isCreating, setIsCreating] = useState(false)
  const [rotatingId, setRotatingId] = useState<number | null>(null)
  const [revokingId, setRevokingId] = useState<number | null>(null)

  const query = useQuery<ApiToken[], { status?: number }>({
    queryKey: ['api-tokens'],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.GET('/api/v2/api-tokens', {})
      if (result.error) {
        const err = result.error as { status?: number }
        throw err
      }
      return (result.data as ApiTokensListResponse).data
    },
  })

  const isPremiumError =
    query.isError &&
    !!query.error &&
    (query.error as { status?: number }).status === 402

  const createToken = useCallback(async (name: string): Promise<ApiTokenWithSecret> => {
    setIsCreating(true)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.POST('/api/v2/api-tokens', {
        body: { name },
      })
      if (result.error) {
        throw result.error
      }
      const created = (result.data as ApiTokenCreateResponse).data
      // Neue Token zur Liste hinzufügen (ohne secret token-Feld)
      const listToken: ApiToken = {
        id: created.id,
        name: created.name,
        last_used_at: null,
        created_at: created.created_at,
      }
      queryClient.setQueryData<ApiToken[]>(['api-tokens'], (prev = []) => [listToken, ...prev])
      return created
    } finally {
      setIsCreating(false)
    }
  }, [queryClient])

  const rotateToken = useCallback(async (id: number): Promise<ApiTokenWithSecret> => {
    setRotatingId(id)
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.PATCH(`/api/v2/api-tokens/${id}/rotate`, {})
      if (result.error) {
        throw result.error
      }
      const rotated = (result.data as ApiTokenRotateResponse).data
      // Erstell-Datum in der Liste aktualisieren
      queryClient.setQueryData<ApiToken[]>(['api-tokens'], (prev = []) =>
        prev.map(t =>
          t.id === id ? { ...t, created_at: rotated.created_at } : t
        )
      )
      return rotated
    } finally {
      setRotatingId(null)
    }
  }, [queryClient])

  const revokeToken = useCallback(async (id: number): Promise<void> => {
    setRevokingId(id)
    // Optimistic: sofort aus der Liste entfernen
    const previous = queryClient.getQueryData<ApiToken[]>(['api-tokens']) ?? []
    queryClient.setQueryData<ApiToken[]>(['api-tokens'], (prev = []) => prev.filter(t => t.id !== id))
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.DELETE(`/api/v2/api-tokens/${id}`, {})
      if (result.error) {
        throw result.error
      }
    } catch (err) {
      // Rollback bei Fehler
      queryClient.setQueryData<ApiToken[]>(['api-tokens'], previous)
      throw err
    } finally {
      setRevokingId(null)
    }
  }, [queryClient])

  return {
    tokens: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError && !isPremiumError,
    isPremiumError,
    refetch: () => query.refetch(),
    createToken,
    rotateToken,
    revokeToken,
    isCreating,
    rotatingId,
    revokingId,
  }
}
